import { useState, useEffect } from 'react';
import { callRpc } from '../../lib/rpcClient';
import { Clock, RefreshCw, Smartphone, Server, MapPin, MousePointer2, ChevronDown, Shield } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function AdminLoginTimes() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const now = new Date();
      const from = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString(); // 14 days
      const to = new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString(); // +1 day to be safe
      
      const result = await callRpc('get_admin_login_events', { 
        p_from: from, 
        p_to: to,
        p_limit: 200
      });
      
      setData(result || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load login logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getStatusBadge = (code: number | null, message?: string) => {
    const isSuccess = (code && code >= 200 && code < 300) || message?.toLowerCase().includes('success');
    if (isSuccess) {
      return <span className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">Success</span>;
    }
    return <span className="bg-red-500/10 text-red-500 border border-red-500/20 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">Failed</span>;
  };

  const formatProviderName = (name: string | undefined) => {
    if (!name) return 'magic_link';
    // Strip prefixes like success_ google_ or initiated_
    return name.replace(/^(success_|restore_|initiated_)/, '');
  };

  return (
    <div className="space-y-6 font-primary pb-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-white mb-1 flex items-center gap-3">
            <Clock className="text-blue-500" size={32} />
            Login Times & Access
          </h1>
          <p className="text-slate-400 font-bold">Monitoring authentication attempts, magic links, and provider success rates.</p>
        </div>
        
        <button 
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all font-black text-sm shadow-lg shadow-blue-500/20 active:scale-95 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          <span>Refresh Logs</span>
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
        <div className="overflow-x-auto min-h-[400px]">
          {loading ? (
             <div className="flex flex-col items-center justify-center p-24 gap-4">
               <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
               <p className="text-slate-500 font-bold uppercase text-xs tracking-[0.2em]">Analyzing Auth Traffic...</p>
             </div>
          ) : error ? (
            <div className="p-16 text-center">
                <p className="text-red-500 font-bold mb-4">{error}</p>
                <button onClick={loadData} className="bg-red-500 text-white px-6 py-2 rounded-xl font-bold">Retry</button>
            </div>
          ) : data.length === 0 ? (
            <div className="p-24 text-center flex flex-col items-center text-slate-500">
               <Shield size={64} className="text-blue-500/20 mb-6" />
               <p className="text-xl font-black text-white mb-2">No auth logs found</p>
               <p className="font-medium">No authentication activity recorded in the last 14 days.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/50">
              {data.map((item) => {
                const isExpanded = expandedId === item.id;
                const metadata = item.metadata || {};
                
                return (
                  <div key={item.id} className="flex flex-col transition-all hover:bg-white/5 group border-l-4 border-l-transparent hover:border-l-blue-500">
                    <div 
                      className="p-6 flex cursor-pointer items-center justify-between"
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    >
                      <div className="flex items-center gap-6 flex-1 min-w-0 pr-4">
                        <div className="shrink-0 w-12 h-12 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                          {item.path?.includes('magic-link') ? <Server size={22} className="text-purple-400" /> : <Smartphone size={22} className="text-blue-400" />}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1.5">
                            {getStatusBadge(item.status_code, item.message)}
                            <span className="font-black text-white text-lg truncate">
                              {item.user_email || 'Anonymous Access'}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-bold text-slate-500 uppercase tracking-tight">
                            <span className="flex items-center gap-1.5"><MousePointer2 size={12} className="text-slate-700" /> {item.context || 'auth_event'}</span>
                            <span>•</span>
                            <span className="text-slate-400">{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
                            {metadata?.location && (
                                <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1.5 text-blue-400/80"><MapPin size={12} /> {metadata.location}</span>
                                </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="hidden lg:flex flex-col items-end">
                            <p className="text-[10px] text-slate-600 font-black uppercase mb-1">Provider</p>
                            <p className="text-xs text-slate-400 font-bold">{formatProviderName(metadata?.provider)}</p>
                        </div>
                        <ChevronDown size={20} className={`text-slate-600 transition-all duration-300 ${isExpanded ? 'rotate-180 text-blue-500' : ''}`} />
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="px-6 pb-8 animate-in slide-in-from-top-2 duration-300">
                        <div className="bg-slate-950/50 rounded-3xl border border-slate-800 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">Metadata Details</p>
                                        <div className="space-y-2">
                                            <div className="flex justify-between p-3 bg-slate-900 border border-slate-800 rounded-xl">
                                                <span className="text-xs text-slate-500 font-bold">IP Address</span>
                                                <span className="text-xs text-white font-mono">{metadata?.ip || 'N/A'}</span>
                                            </div>
                                            <div className="flex justify-between p-3 bg-slate-900 border border-slate-800 rounded-xl">
                                                <span className="text-xs text-slate-500 font-bold">Country</span>
                                                <span className="text-xs text-white font-mono">{metadata?.country_code || 'Unknown'}</span>
                                            </div>
                                            <div className="flex justify-between p-3 bg-slate-900 border border-slate-800 rounded-xl">
                                                <span className="text-xs text-slate-500 font-bold">Browser</span>
                                                <span className="text-xs text-white truncate max-w-[150px]">{metadata?.browser?.split(' ')[0] || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">System Context</p>
                                        <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-3 font-mono text-[11px]">
                                            <div>
                                                <span className="text-blue-400 font-bold mr-2">PATH:</span>
                                                <span className="text-slate-300">{item.path}</span>
                                            </div>
                                            <div>
                                                <span className="text-purple-400 font-bold mr-2">SOURCE:</span>
                                                <span className="text-slate-300">{item.source}</span>
                                            </div>
                                            <div>
                                                <span className="text-emerald-400 font-bold mr-2">STATUS:</span>
                                                <span className="text-slate-300">{item.status_code || 200}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">Extended Logs</p>
                                        <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl min-h-[100px]">
                                            <p className="text-xs text-slate-400 leading-relaxed italic">
                                                {item.message || 'No additional log messages for this attempt.'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
